import type { FC } from 'react';
import type {
  EChartsOption,
  TooltipComponentFormatterCallback,
  TooltipComponentFormatterCallbackParams,
} from 'echarts';
import type { RootState } from '@/store/store';
import {
  ChartsTypeEnum,
  type IChartLabels,
  type IScatterConfigItem,
} from '../../../types';

import { init } from 'echarts';
import { useEffect, useRef, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import type { IScatterSeriesFormatterParams } from './types';
import { DashboardSearchContext } from '@/pages/dashboard-search';

type IScatterChartProps = {
  data: IScatterConfigItem[];
  labels: IChartLabels;
  height: number;
  showChart: boolean;
};

const ScatterChart: FC<IScatterChartProps> = ({
  data,
  labels,
  height,
  showChart,
}) => {
  const { currentTheme } = useSelector((state: RootState) => state.theme);
  const sharesRef = useRef<HTMLDivElement | null>(null);
  const { colorMap, onlyMyBrandsMap } = useContext(DashboardSearchContext);

  const options: EChartsOption = useMemo(() => {
    const formatter: TooltipComponentFormatterCallback<
      TooltipComponentFormatterCallbackParams & IScatterSeriesFormatterParams
    > = (params) => {
      return `
        ${params.data.name}
        </br>
        ${labels.x}: ${params?.data?.value?.[0]}
        </br>
        ${labels.y}: ${params?.data?.value?.[1]}
        `;
    };

    return {
      tooltip: {
        formatter: formatter as unknown,
      },
      xAxis: {
        type: 'value',
        name: labels?.x,
        nameLocation: 'middle',
        nameGap: 25,
        axisLine: {
          onZero: false,
        },
        // max: (value) => value.max + value.max / 20,
        // min: (value) => value.min - value.min / 20,
      },
      yAxis: {
        type: 'value',
        name: labels?.y,
        inverse: true,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 25,
        // max: (value) => value.max + value.max / 20,
        // min: (value) => value.min - value.min / 20,
      },
      dataZoom: [
        {
          type: 'inside',
        },
        {
          type: 'inside',
        },
      ],

      series: [
        {
          label: {
            textBorderColor: currentTheme === 'dark' ? 'transparent' : 'white',
            textBorderWidth: 1,
            color: currentTheme === 'dark' ? 'white' : 'black',
            show: true,
            position: 'top',
            formatter: '{b}',
          },
          data: data.map((scatterConfigItem) => ({
            ...scatterConfigItem,
            itemStyle: {
              color:
                onlyMyBrandsMap?.[scatterConfigItem.name] ??
                colorMap.get(scatterConfigItem.name),
            },
            symbolSize: Math.sqrt(scatterConfigItem.symbolSize * 200),
          })),
          type: ChartsTypeEnum.scatter,
          itemStyle: {
            // color: themeColors.primary[800],
            borderColor: currentTheme === 'dark' ? 'white' : 'black',
            borderWidth: 2,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            shadowBlur: 10,
          },
          emphasis: {
            focus: 'self',
          },
        },
      ],
    } as EChartsOption;
  }, [data, currentTheme, labels, colorMap]);

  useEffect(() => {
    const myChart = init(sharesRef.current);
    showChart && myChart.setOption(options);
    return () => myChart.dispose();
  }, [options, height, showChart]);

  return (
    <div
      className={`${height === 500 ? 'h-[500px]' : 'h-[300px]'} w-full`}
      ref={sharesRef}
    />
  );
};

export default ScatterChart;
